import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Location } from '../types/vrp';
import { useDeleteLocation } from './useLocations';

type BulkActionType = '' | 'delete' | 'export';

interface LocationsPageState {
  isFormOpen: boolean;
  isBulkImportOpen: boolean;
  isBulkExportOpen: boolean;
  isViewDialogOpen: boolean;
  selectedLocation: Location | null;
  editingLocation: Location | null;
  viewingLocation: Location | null;
  selectedRows: number[];
  bulkAction: BulkActionType;
  anchorEl: HTMLElement | null;
}

export const useLocationsState = (currentProject: any) => {
  const [state, setState] = useState<LocationsPageState>({
    isFormOpen: false,
    isBulkImportOpen: false,
    isBulkExportOpen: false,
    isViewDialogOpen: false,
    selectedLocation: null,
    editingLocation: null,
    viewingLocation: null,
    selectedRows: [],
    bulkAction: '' as BulkActionType,
    anchorEl: null,
  });

  const { mutate: deleteLocation } = useDeleteLocation();
  const queryClient = useQueryClient();

  // Form and dialog handlers
  const openForm = useCallback(() => {
    setState(prev => ({ ...prev, isFormOpen: true }));
  }, []);

  const closeForm = useCallback(() => {
    setState(prev => ({ 
      ...prev, 
      isFormOpen: false, 
      editingLocation: null 
    }));
  }, []);

  const openBulkImport = useCallback(() => {
    setState(prev => ({ ...prev, isBulkImportOpen: true }));
  }, []);

  const closeBulkImport = useCallback(() => {
    setState(prev => ({ ...prev, isBulkImportOpen: false }));
  }, []);

  const openBulkExport = useCallback(() => {
    setState(prev => ({ ...prev, isBulkExportOpen: true }));
  }, []);

  const closeBulkExport = useCallback(() => {
    setState(prev => ({ ...prev, isBulkExportOpen: false }));
  }, []);
  // Location operation handlers
  const viewLocation = useCallback((location: Location) => {
    setState(prev => ({
      ...prev,
      viewingLocation: location,
      isViewDialogOpen: true,
      anchorEl: null,
    }));
  }, []);

  const editLocation = useCallback((location: Location) => {
    setState(prev => ({
      ...prev,
      editingLocation: location,
      isFormOpen: true,
      anchorEl: null,
    }));
  }, []);

  const deleteLocationHandler = useCallback((location: Location) => {
    if (currentProject && window.confirm(`Are you sure you want to delete location "${location.name}"?`)) {
      deleteLocation({ 
        id: location.id, 
        projectId: currentProject.id 
      });
    }
    setState(prev => ({ ...prev, anchorEl: null }));
  }, [currentProject, deleteLocation]);

  const closeViewDialog = useCallback(() => {
    setState(prev => ({ 
      ...prev, 
      isViewDialogOpen: false, 
      viewingLocation: null 
    }));
  }, []);

  // Selection handlers
  const selectRow = useCallback((locationId: number) => {
    setState(prev => ({
      ...prev,
      selectedRows: prev.selectedRows.includes(locationId)
        ? prev.selectedRows.filter(id => id !== locationId)
        : [...prev.selectedRows, locationId]
    }));
  }, []);

  const selectAllRows = useCallback((locations: Location[], checked: boolean) => {
    setState(prev => ({
      ...prev,
      selectedRows: checked ? locations.map(location => location.id) : []
    }));
  }, []);

  const clearSelection = useCallback(() => {
    setState(prev => ({
      ...prev,
      selectedRows: [],
      bulkAction: '' as BulkActionType
    }));
  }, []);
  // Bulk action handlers
  const setBulkAction = useCallback((action: BulkActionType) => {
    setState(prev => ({ ...prev, bulkAction: action }));
  }, []);

  const applyBulkAction = useCallback(async () => {
    if (!state.bulkAction || state.selectedRows.length === 0 || !currentProject) return;
    
    if (state.bulkAction === 'delete') {
      if (window.confirm(`Are you sure you want to delete ${state.selectedRows.length} location(s)?`)) {
        try {
          for (const locationId of state.selectedRows) {
            await new Promise<void>((resolve, reject) => {
              deleteLocation(
                { id: locationId, projectId: currentProject.id },
                {
                  onSuccess: () => resolve(),
                  onError: (error) => reject(error)
                }
              );
            });
          }
          clearSelection();
        } catch (error) {
          console.error('❌ Bulk delete failed:', error);
          alert('Some locations could not be deleted. Please try again.');
        }
      }
    }
  }, [state.bulkAction, state.selectedRows, currentProject, deleteLocation, clearSelection]);

  // Import success handler
  const handleImportSuccess = useCallback(() => {
    if (!currentProject) return;
    
    console.log('🔄 [useLocationsState] Invalidating queries after import for project:', currentProject.id);
    
    // Invalidate ALL location-related queries for this project
    queryClient.invalidateQueries({ queryKey: ['locations', currentProject.id] });
    queryClient.invalidateQueries({ queryKey: ['locations'] });
    
    // Force refetch of locations
    queryClient.refetchQueries({ queryKey: ['locations', currentProject.id] });
    
    // Close the import dialog
    setState(prev => ({ ...prev, isBulkImportOpen: false }));
    
    console.log('✅ [useLocationsState] Import success handling complete');
  }, [currentProject, queryClient]);

  return {
    state,
    handlers: {
      openForm,
      closeForm,
      openBulkImport,
      closeBulkImport,
      openBulkExport,
      closeBulkExport,
      viewLocation,
      editLocation,
      deleteLocation: deleteLocationHandler,
      closeViewDialog,
      selectRow,
      selectAllRows,
      clearSelection,
      setBulkAction,
      applyBulkAction,
      handleImportSuccess,
    },
  };
};
